import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Globe, Workflow } from "lucide-react";
import useScrollReveal from "../../hooks/useScrollReveal";
import Nav from "../site/Nav";
import SEO from "../site/SEO";
import Footer from "../site/Footer";
import {
  PackageGrid,
  PackageCard,
  ComparisonSection,
  ProcessSection,
  FAQSection,
} from "../site/OfferingPrimitives";
import {
  WEBSITE_PACKAGES,
  LANDING_PAGE_PACKAGE,
  toPackagesCardShape,
} from "../../data/websitePackages";

const openAudit = () => window.dispatchEvent(new Event("open-audit"));

/* ---------- Data ---------- */

const websitePackages = WEBSITE_PACKAGES.map(toPackagesCardShape);
const landingPagePackage = toPackagesCardShape(LANDING_PAGE_PACKAGE);

const websiteProblems = [
  "Outdated design",
  "Weak mobile layout",
  "Confusing service pages",
  "No clear call to action",
  "Poor trust and proof",
];

const revenueProblems = [
  "Leads are not followed up with quickly",
  "Forms do not connect to a CRM",
  "Booking flow is unclear",
  "No automated reminders",
  "No reactivation process",
  "No visibility into where leads came from",
];

const faqs = [
  {
    q: "Do I need a website or a revenue system?",
    a: "If you mainly need credibility and a better online presence, start with a website package. If you already get leads but lose them because follow-up, booking, or CRM flow is messy, start with a revenue system.",
  },
  {
    q: "Can I start with a website and add systems later?",
    a: "Yes. Website packages can be built first, then upgraded with lead capture, CRM setup, booking automation, follow-up, and reactivation systems later.",
  },
  {
    q: "What is the Revenue Audit?",
    a: "The Revenue Audit identifies where your business is leaking leads, bookings, and revenue. It reviews your website, forms, follow-up, booking flow, CRM setup, and automation opportunities.",
  },
  {
    q: "Do you only work with service businesses?",
    a: "The Modern Exit is best suited for service businesses, coaches, consultants, trainers, local businesses, and operators who need clearer paths from attention to revenue.",
  },
  {
    q: "Do you build automations too?",
    a: "Yes. Revenue Systems Packages can include lead intake, CRM setup, follow-up automation, booking reminders, internal notifications, lead source tracking, and reactivation flows.",
  },
];

/* ---------- Page ---------- */

export default function PackagesPage() {
  useScrollReveal();
  const navigate = useNavigate();

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goToBuildBrief = () => navigate("/build-brief");
  const goToRevenueSystems = () => navigate("/revenue-systems");

  return (
    <div className="bg-sand-100 text-ink min-h-screen" data-testid="packages-page">
      <SEO page="/packages" />
      <Nav />
      <main>
        <HeroSection
          scrollToId={scrollToId}
          goToRevenueSystems={goToRevenueSystems}
        />
        <PathSelector
          scrollToId={scrollToId}
          goToRevenueSystems={goToRevenueSystems}
        />
        <ProblemSolutionSection />
        <LandingPageRow onCta={goToBuildBrief} />
        <PackageGrid
          id="website-packages"
          tone="light"
          eyebrow="Website Packages"
          title="Built to launch fast."
          subtitle="For businesses that need a clean, modern, conversion-ready website before adding a full revenue system."
          packages={websitePackages}
          onCta={goToBuildBrief}
        />
        <ComparisonSection
          ctaLabel="Start Revenue Audit"
          onCta={goToRevenueSystems}
        />
        <ProcessSection />
        <FAQSection faqs={faqs} />
        <FinalCTASection
          scrollToId={scrollToId}
          goToRevenueSystems={goToRevenueSystems}
        />
      </main>
      <Footer />
    </div>
  );
}

/* ---------- Sections ---------- */

function HeroSection({ scrollToId, goToRevenueSystems }) {
  return (
    <section
      data-testid="offerings-hero"
      className="pt-24 sm:pt-28 md:pt-32 pb-16 sm:pb-20 md:pb-24 px-5 sm:px-6 md:px-10"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-end">
          <div className="lg:col-span-8 reveal">
            <p className="eyebrow">All Offerings</p>
            <h1 data-kinetic className="font-display mt-5 text-[2.1rem] sm:text-[2.6rem] md:text-5xl lg:text-[3.6rem] xl:text-[4rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
              Choose the build that matches{" "}
              <span className="italic text-ink-muted font-normal">
                where your business is right now.
              </span>
            </h1>
          </div>
          <div className="lg:col-span-4 reveal">
            <p className="text-base md:text-lg text-ink-muted leading-relaxed">
              Some businesses need a better website. Others need the systems
              behind the website fixed. The Modern Exit separates both paths
              so you only pay for what actually moves revenue.
            </p>
          </div>
        </div>

        <div className="mt-10 sm:mt-12 reveal flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => scrollToId("website-packages")}
            data-testid="hero-secondary-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-ink-deep text-ink px-7 py-4 text-[15px] font-medium hover:bg-sand-200 transition-colors min-h-[52px]"
          >
            View Website Packages
          </button>
          <button
            type="button"
            onClick={goToRevenueSystems}
            data-testid="hero-primary-cta"
            data-magnetic
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-ink-deep text-sand-100 px-7 py-4 text-[15px] font-medium hover:bg-rust transition-colors min-h-[52px]"
          >
            Start With a Revenue Audit
            <ArrowRight
              size={16}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </button>
        </div>
      </div>
    </section>
  );
}

function PathSelector({ scrollToId, goToRevenueSystems }) {
  return (
    <section
      data-testid="path-selector"
      className="pb-16 sm:pb-20 md:pb-24 px-5 sm:px-6 md:px-10"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-6 items-end mb-10 sm:mb-12 reveal">
          <div className="lg:col-span-7">
            <p className="eyebrow">Choose Your Path</p>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-[3rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
              Start with the service that matches{" "}
              <span className="italic text-ink-muted font-normal">
                the problem you are actually trying to solve.
              </span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          <PathCard
            testId="path-card-websites"
            tag="Website Packages"
            icon={Globe}
            body="For businesses that need a clean, modern, conversion-ready website that makes the brand look credible and gives prospects a clear way to take action."
            ctaLabel="View Website Packages"
            onClick={() => scrollToId("website-packages")}
            tone="light"
          />
          <PathCard
            testId="path-card-revenue"
            tag="Revenue Systems"
            icon={Workflow}
            body="For businesses that already have attention, leads, traffic, or demand — but are losing money because the follow-up, booking, CRM, payments, or automation flow is broken."
            ctaLabel="View Revenue Systems"
            onClick={goToRevenueSystems}
            tone="dark"
          />
        </div>
      </div>
    </section>
  );
}

function PathCard({ testId, tag, icon: Icon, body, ctaLabel, onClick, tone }) {
  const dark = tone === "dark";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`reveal text-left group relative rounded-3xl border p-7 sm:p-9 md:p-10 flex flex-col min-h-[300px] sm:min-h-[340px] overflow-hidden transition-all hover:-translate-y-0.5 ${
        dark
          ? "bg-ink-deep border-ink-deep text-sand-100"
          : "bg-sand-50 border-sand-300 text-ink"
      }`}
    >
      {dark && (
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(560px 320px at 12% 20%, rgba(165,106,67,0.45), transparent 60%), radial-gradient(560px 320px at 90% 80%, rgba(111,115,94,0.22), transparent 60%)",
          }}
        />
      )}

      <div className="relative flex items-center justify-between">
        <span
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] ${
            dark
              ? "bg-white/8 text-sand-300 border border-white/10"
              : "bg-sand-100 text-clay border border-sand-300"
          }`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-clay" />
          {tag}
        </span>
        <span
          className={`h-11 w-11 grid place-items-center rounded-2xl ${
            dark
              ? "bg-white/8 border border-white/10 text-sand-100"
              : "bg-sand-100 border border-sand-300 text-ink"
          }`}
        >
          <Icon size={18} strokeWidth={1.6} />
        </span>
      </div>

      <p
        className={`relative mt-7 sm:mt-9 text-[15px] sm:text-base md:text-lg leading-relaxed max-w-md ${
          dark ? "text-sand-300/85" : "text-ink-muted"
        }`}
      >
        {body}
      </p>

      <div className="relative mt-auto pt-9 flex items-center justify-between">
        <span
          className={`inline-flex items-center gap-2 text-[14px] font-medium ${
            dark ? "text-sand-100" : "text-ink"
          }`}
        >
          {ctaLabel}
          <ArrowRight
            size={15}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </span>
      </div>
    </button>
  );
}

function ProblemSolutionSection() {
  return (
    <section
      data-testid="problem-solution"
      className="py-16 sm:py-20 md:py-24 px-5 sm:px-6 md:px-10 border-t border-sand-300"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-10 sm:mb-14 reveal">
          <div className="lg:col-span-7">
            <p className="eyebrow !text-clay">The Real Problem</p>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-[3rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
              A website alone does not fix{" "}
              <span className="italic text-ink-muted font-normal">
                a broken revenue flow.
              </span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-ink-muted leading-relaxed">
              Most businesses do not just have a website problem. They have a
              lead-to-revenue problem. Leads come in from forms, DMs, ads,
              referrals, or calls — but the follow-up is slow, the booking
              path is unclear, the CRM is messy, and the owner has no simple
              way to see what is converting. The Modern Exit separates website
              builds from revenue systems so the solution matches the actual
              leak.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          <ProblemColumn
            testId="problem-col-website"
            eyebrow="Website Problem Signs"
            items={websiteProblems}
            tone="light"
          />
          <ProblemColumn
            testId="problem-col-revenue"
            eyebrow="Revenue System Problem Signs"
            items={revenueProblems}
            tone="dark"
          />
        </div>
      </div>
    </section>
  );
}

function ProblemColumn({ testId, eyebrow, items, tone }) {
  const dark = tone === "dark";
  return (
    <div
      data-testid={testId}
      className={`reveal rounded-3xl border p-7 sm:p-9 ${
        dark
          ? "bg-ink-deep border-ink-deep text-sand-100"
          : "bg-sand-50 border-sand-300 text-ink"
      }`}
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-clay">
        {eyebrow}
      </p>
      <ul className="mt-6 space-y-3">
        {items.map((it) => (
          <li
            key={it}
            className={`flex items-start gap-3 text-[14.5px] sm:text-[15px] leading-relaxed ${
              dark ? "text-sand-100" : "text-ink"
            }`}
          >
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-clay shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LandingPageRow({ onCta }) {
  return (
    <section
      data-testid="landing-page-row"
      className="px-5 sm:px-6 md:px-10 pt-20 sm:pt-24 md:pt-28 pb-2"
    >
      <div className="max-w-7xl mx-auto">
        <div className="reveal mb-6 sm:mb-8 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay">
            Add-on · Campaign Page
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {/* Spacer · only on lg to center the card in the middle column */}
          <div className="hidden lg:block" aria-hidden="true" />
          <PackageCard pkg={landingPagePackage} onCta={onCta} tone="light" />
          <div className="hidden lg:block" aria-hidden="true" />
        </div>
      </div>
    </section>
  );
}

function FinalCTASection({ scrollToId, goToRevenueSystems }) {
  return (
    <section
      id="contact"
      data-testid="final-cta"
      className="scroll-mt-24 sm:scroll-mt-28 py-20 sm:py-24 md:py-28 px-5 sm:px-6 md:px-10 border-t border-sand-300"
    >
      <div className="max-w-3xl mx-auto text-center reveal">
        <p className="eyebrow">Final Step</p>
        <h2 className="font-display mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-[3rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
          Not sure which build{" "}
          <span className="italic text-ink-muted font-normal">
            fits your business?
          </span>
        </h2>
        <p className="mt-6 text-base md:text-lg text-ink-muted leading-relaxed max-w-xl mx-auto">
          Start with a Revenue Audit. We'll identify whether your business
          needs a better website, a stronger follow-up system, or a complete
          revenue flow.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={goToRevenueSystems}
            data-testid="final-primary-cta"
            data-magnetic
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-ink-deep text-sand-100 px-7 py-4 text-[15px] font-medium hover:bg-rust transition-colors min-h-[52px]"
          >
            Start With a Revenue Audit
            <ArrowRight
              size={16}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </button>
          <button
            type="button"
            onClick={() => scrollToId("website-packages")}
            data-testid="final-secondary-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-ink-deep text-ink px-7 py-4 text-[15px] font-medium hover:bg-sand-200 transition-colors min-h-[52px]"
          >
            View Website Packages
          </button>
        </div>
      </div>
    </section>
  );
}
